import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  AlertCircle,
  Loader2,
  Pause,
  Play,
  Archive,
  Mail,
  Eye,
  MousePointerClick,
  UserX,
  Edit3,
  ExternalLink,
} from 'lucide-react';
import { leadCampaignsAPI, leadSegmentsAPI } from '../utils/api';
import {
  CampaignState,
  DeliveryFilterState,
  DELIVERY_FILTER_STATES,
  DELIVERY_FILTER_STATE_LABEL,
} from '../types/leadCampaign';
import LeadCampaignStateBadge from '../components/lead/LeadCampaignStateBadge';
import ConfirmModal from '../components/ConfirmModal';
import { usePermissions } from '../hooks/usePermissions';

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('it-IT');
}

function pct(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

export default function LeadCampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('leads:config:edit');

  const [deliveryFilter, setDeliveryFilter] = useState<DeliveryFilterState>('all');
  const [deliveryPage, setDeliveryPage] = useState(1);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const {
    data: campaign,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['campaign', id],
    queryFn: async () => {
      const res = await leadCampaignsAPI.getCampaign(id!);
      return res.data;
    },
    enabled: !!id,
  });

  const { data: metrics } = useQuery({
    queryKey: ['campaign-metrics', id],
    queryFn: async () => {
      const res = await leadCampaignsAPI.getMetrics(id!);
      return res.data;
    },
    enabled: !!id,
    refetchInterval: 30_000,
  });

  const { data: steps } = useQuery({
    queryKey: ['campaign-steps', id],
    queryFn: async () => {
      const res = await leadCampaignsAPI.getSteps(id!);
      return res.data;
    },
    enabled: !!id,
  });

  const { data: segment } = useQuery({
    queryKey: ['segment', campaign?.segment_id],
    queryFn: async () => {
      const res = await leadSegmentsAPI.getSegment(campaign!.segment_id);
      return res.data;
    },
    enabled: !!campaign?.segment_id,
  });

  const { data: deliveriesData, isLoading: isLoadingDeliveries } = useQuery({
    queryKey: ['campaign-deliveries', id, deliveryFilter, deliveryPage],
    queryFn: async () => {
      const res = await leadCampaignsAPI.getDeliveries(id!, {
        state: deliveryFilter,
        page: deliveryPage,
        limit: 50,
      });
      return res.data;
    },
    enabled: !!id,
  });

  const transitionMutation = useMutation({
    mutationFn: (action: 'pause' | 'resume' | 'archive') => {
      if (action === 'pause') return leadCampaignsAPI.pause(id!);
      if (action === 'resume') return leadCampaignsAPI.resume(id!);
      return leadCampaignsAPI.archive(id!);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success(`Stato cambiato a ${res.data.state}`);
      setConfirmArchive(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella transizione');
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (isError || !campaign) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Campagna non trovata</h2>
        <Link
          to="/leads/campaigns"
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          Torna alle campagne
        </Link>
      </div>
    );
  }

  const state: CampaignState = campaign.state;
  const deliveries = deliveriesData?.data || [];
  const deliveryPagination = deliveriesData?.pagination;
  const stepById = new Map((steps || []).map((s) => [s.id, s]));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4 flex-wrap">
          <button
            onClick={() => navigate('/leads/campaigns')}
            className="inline-flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Campagne
          </button>
          <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
          <LeadCampaignStateBadge state={state} />
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => navigate(`/leads/campaigns/${id}/edit`)}
              disabled={state === 'archived'}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <Edit3 className="h-4 w-4" />
              Modifica
            </button>
            {state === 'active' && (
              <button
                onClick={() => transitionMutation.mutate('pause')}
                disabled={transitionMutation.isPending}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {transitionMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Pause className="h-4 w-4" />
                )}
                Metti in pausa
              </button>
            )}
            {state === 'paused' && (
              <button
                onClick={() => transitionMutation.mutate('resume')}
                disabled={transitionMutation.isPending}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {transitionMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Play className="h-4 w-4" />
                )}
                Riprendi
              </button>
            )}
            {state !== 'archived' && (
              <button
                onClick={() => setConfirmArchive(true)}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-400 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <Archive className="h-4 w-4" />
                Archivia
              </button>
            )}
          </div>
        )}
      </div>

      {/* Summary card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">Segmento</div>
            <div className="text-gray-900 font-medium mt-0.5">
              {segment?.name || (
                <span className="text-gray-400 italic">caricamento…</span>
              )}
            </div>
            {segment && (
              <Link
                to={`/leads?segment_id=${segment.id}`}
                className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 mt-1"
              >
                <ExternalLink className="h-3 w-3" />
                Apri lead del segmento
              </Link>
            )}
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">From</div>
            <div className="text-gray-900 mt-0.5">
              {campaign.from_name} &lt;{campaign.from_email}&gt;
            </div>
            {campaign.reply_to && (
              <div className="text-xs text-gray-500">reply-to: {campaign.reply_to}</div>
            )}
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">Tempistiche</div>
            <div className="text-gray-900 mt-0.5">
              Avviata: <span className="font-medium">{formatDate(campaign.started_at)}</span>
            </div>
            {campaign.archived_at && (
              <div className="text-gray-700">
                Archiviata: {formatDate(campaign.archived_at)}
              </div>
            )}
            <div className="text-xs text-gray-500">
              {steps?.length ?? 0} step configurati
            </div>
          </div>
        </div>
      </div>

      {/* Metrics cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          icon={<Mail className="h-5 w-5 text-blue-600" />}
          label="Inviate"
          value={metrics?.sent ?? '—'}
        />
        <MetricCard
          icon={<Eye className="h-5 w-5 text-emerald-600" />}
          label="Open rate"
          value={metrics ? pct(metrics.open_rate) : '—'}
          sub={metrics ? `${metrics.opened} aperte` : undefined}
        />
        <MetricCard
          icon={<MousePointerClick className="h-5 w-5 text-indigo-600" />}
          label="Click rate"
          value={metrics ? pct(metrics.click_rate) : '—'}
          sub={metrics ? `${metrics.clicked} click` : undefined}
        />
        <MetricCard
          icon={<UserX className="h-5 w-5 text-red-600" />}
          label="Unsubscribe rate"
          value={metrics ? pct(metrics.unsubscribe_rate) : '—'}
          sub={metrics ? `${metrics.unsubscribed} unsubs` : undefined}
        />
      </div>

      {/* Deliveries table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-lg font-semibold text-gray-900">Deliveries</h3>
          <div className="flex items-center gap-1 flex-wrap">
            {DELIVERY_FILTER_STATES.map((s) => (
              <button
                key={s}
                onClick={() => {
                  setDeliveryFilter(s);
                  setDeliveryPage(1);
                }}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  deliveryFilter === s
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {DELIVERY_FILTER_STATE_LABEL[s]}
              </button>
            ))}
          </div>
        </div>
        {isLoadingDeliveries ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : deliveries.length === 0 ? (
          <div className="text-center py-10 text-sm text-gray-500">
            Nessuna delivery per il filtro corrente
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lead
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Step
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Opened
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Clicked
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unsub
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bounce
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {deliveries.map((d) => {
                const step = stepById.get(d.step_id);
                return (
                  <tr key={d.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm">
                      <Link
                        to={`/leads/${d.lead_id}`}
                        className="text-blue-600 hover:text-blue-800 font-mono text-xs"
                      >
                        {d.lead_id.slice(0, 8)}…
                      </Link>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-700">
                      {step ? `#${step.ordine + 1} · ${step.subject}` : d.step_id.slice(0, 8)}
                    </td>
                    <td className="px-6 py-3 text-xs text-gray-600">
                      {formatDate(d.sent_at)}
                    </td>
                    <td className="px-6 py-3 text-xs">
                      {d.opened_at ? (
                        <span className="text-emerald-700 font-medium">
                          {formatDate(d.opened_at)}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-xs">
                      {d.clicked_at ? (
                        <span className="text-indigo-700 font-medium">
                          {formatDate(d.clicked_at)}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-xs">
                      {d.unsubscribed_at ? (
                        <span className="text-red-700 font-medium">
                          {formatDate(d.unsubscribed_at)}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-xs text-amber-700 max-w-xs truncate">
                      {d.bounce_reason || (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {deliveryPagination && deliveryPagination.pages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Pagina {deliveryPagination.page} di {deliveryPagination.pages} (
              {deliveryPagination.total} delivery)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setDeliveryPage(deliveryPage - 1)}
                disabled={deliveryPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Precedente
              </button>
              <button
                onClick={() => setDeliveryPage(deliveryPage + 1)}
                disabled={deliveryPage >= deliveryPagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
              >
                Successivo
              </button>
            </div>
          </div>
        )}
      </div>

      {confirmArchive && (
        <ConfirmModal
          isOpen={confirmArchive}
          title="Archivia campagna"
          message={
            'Archiviare questa campagna? Lo stato è terminale: la campagna non potrà più essere ' +
            'riavviata. Le metriche e la cronologia delivery rimangono consultabili.'
          }
          confirmLabel="Archivia"
          onConfirm={() => transitionMutation.mutate('archive')}
          onClose={() => setConfirmArchive(false)}
          isLoading={transitionMutation.isPending}
          variant="warning"
        />
      )}
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wider">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}
